'use client'

import { HTMLAttributes } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import clsx from 'classnames'
import useEmblaCarousel from 'embla-carousel-react'
import { useLocale, useTranslations } from 'next-intl'
import { useAppearance, variantsDefault } from '@/shared/utils'
import { SymbolCard } from '@/entities/symbol-card'
import { SymbolCardProps } from '@/entities/symbol-card/symbol-card'
import { Text } from '@/shared/ui'
import style from './symbols.module.scss'

interface SymbolsProps extends HTMLAttributes<HTMLDivElement> {
  symbols: SymbolCardProps[]
}

export const Symbols = (props: SymbolsProps) => {
  const { className, symbols } = props

  const { ref: cardsRef, animate: cardsAnimate } = useAppearance()
  const [emblaRef] = useEmblaCarousel({
    align: 'start',
    container: `.${style.cards}`,
    breakpoints: {
      '(min-width: 1024px)': { active: false },
      '(max-width: 768px)': { align: 'center' },
    },
  })

  const currentLocale = useLocale()
  const translate = useTranslations()

  const filteredSymbols = symbols.filter(symbol => !symbol.locale || symbol.locale === currentLocale)

  return (
    <section className={clsx(style.container, 'main-padding', 'white-section', className)}>
      <Text className={style.title} color="dark" type="h2" tagName="h3">
        {translate('officialSymbols')}
      </Text>
      <ul className={style.wrapper}>
        {filteredSymbols.map((symbol, index) => (
          <SymbolCard key={index} src={symbol.src} alt={symbol.alt} downloadLink={symbol.downloadLink} />
        ))}
      </ul>
      <div className={style.slider} ref={emblaRef}>
        <motion.ul
          className={clsx(style.list)}
          ref={cardsRef}
          initial="hidden"
          animate={cardsAnimate}
          variants={variantsDefault}
        >
          <AnimatePresence mode="popLayout">
            {filteredSymbols.map((symbol, index) => (
              <SymbolCard
                className={style.item}
                key={index}
                src={symbol.src}
                alt={symbol.alt}
                downloadLink={symbol.downloadLink}
              />
            ))}
          </AnimatePresence>
        </motion.ul>
      </div>
    </section>
  )
}
